// FTEDN Team — Chat Admin Page (DM creators, view all channels, moderate)
import { useEffect, useState, useRef } from 'react';
import { apiClient } from '../../hooks/useApi';
import './ChatAdmin.css';

interface Channel {
  id: string;
  type: string;
  name: string | null;
  memberIds: string[];
  createdBy: string;
  lastMessageAt: string | null;
}

interface Message {
  id: string;
  channelId: string;
  senderId: string;
  text: string;
  hidden: boolean;
  createdAt: string;
}

export function ChatAdminPage() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newMsg, setNewMsg] = useState('');
  const [dmTarget, setDmTarget] = useState('');
  const [showDmForm, setShowDmForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEnd = useRef<HTMLDivElement>(null);

  // Fetch all channels (admin view)
  useEffect(() => {
    setLoading(true);
    apiClient.get('/chat/channels/all')
      .then(res => {
        if (res.ok && Array.isArray(res.channels)) setChannels(res.channels);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Fetch messages when channel changes
  useEffect(() => {
    if (!activeId) return;
    apiClient.get(`/chat/channels/${activeId}/messages`)
      .then(res => {
        if (res.ok && Array.isArray(res.messages)) {
          setMessages(res.messages);
          setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
      })
      .catch(() => {});
  }, [activeId]);

  const handleSend = async () => {
    if (!newMsg.trim() || !activeId) return;
    const res = await apiClient.post(`/chat/channels/${activeId}/messages`, { text: newMsg });
    if (res.ok) {
      setMessages(prev => [...prev, { id: res.messageId, channelId: activeId, senderId: 'team', text: newMsg, hidden: false, createdAt: new Date().toISOString() }]);
      setNewMsg('');
      setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const handleHide = async (msgId: string) => {
    if (!activeId) return;
    const res = await apiClient.post(`/chat/channels/${activeId}/hide`, { messageId: msgId });
    if (res.ok) {
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, hidden: true } : m));
    }
  };

  const handleCreateDM = async () => {
    if (!dmTarget.trim()) return;
    const res = await apiClient.post('/chat/channels', { type: 'dm', memberIds: [dmTarget.trim()] });
    if (res.ok && res.channelId) {
      setActiveId(res.channelId);
      setShowDmForm(false);
      setDmTarget('');
      // Refresh channels
      const chRes = await apiClient.get('/chat/channels/all');
      if (chRes.ok) setChannels(chRes.channels);
    }
  };

  const activeChannel = channels.find(c => c.id === activeId);

  return (
    <div className="mgmt-page chat-admin">
      <div className="mgmt-header">
        <h1>💬 Chat</h1>
        <p className="mgmt-subtitle">Message creators directly. View and moderate all conversations.</p>
        <button className="neo-btn neo-btn--team" onClick={() => setShowDmForm(!showDmForm)}>
          {showDmForm ? '✕ Cancel' : '✉️ New DM'}
        </button>
      </div>

      {showDmForm && (
        <div className="dm-form">
          <input className="team-input" placeholder="Creator user ID" value={dmTarget}
            onChange={e => setDmTarget(e.target.value)} />
          <button className="neo-btn neo-btn--team" onClick={handleCreateDM}>Start Chat</button>
        </div>
      )}

      <div className="chat-layout">
        {/* Channel list */}
        <div className="chat-channels">
          <h3>All Channels ({channels.length})</h3>
          {loading ? <p className="chat-loading">Loading...</p> : channels.length === 0 ? (
            <p className="chat-empty">No channels yet</p>
          ) : (
            channels.map(ch => (
              <button key={ch.id} className={`channel-row ${activeId === ch.id ? 'active' : ''}`}
                onClick={() => setActiveId(ch.id)}>
                <span className="channel-icon">{ch.type === 'dm' ? '👤' : ch.type === 'group' ? '👥' : '📢'}</span>
                <div className="channel-info">
                  <span className="channel-name">{ch.name || `${ch.type.toUpperCase()} (${ch.memberIds.length})`}</span>
                  <span className="channel-members">{ch.memberIds.length} members</span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Messages */}
        <div className="chat-messages">
          {!activeId ? (
            <div className="chat-placeholder">
              <span>💬</span>
              <p>Select a channel to view messages</p>
            </div>
          ) : (
            <>
              <div className="chat-messages-header">
                <h3>{activeChannel?.name || `Channel ${activeId.slice(0, 8)}...`}</h3>
                <span className="chat-channel-type">{activeChannel?.type}</span>
              </div>
              <div className="chat-messages-list">
                {messages.filter(m => !m.hidden).map(m => (
                  <div key={m.id} className="chat-msg">
                    <div className="chat-msg-header">
                      <span className="chat-msg-sender">{m.senderId === 'team' ? '👑 Team' : m.senderId.slice(0, 8)}</span>
                      <span className="chat-msg-time">{new Date(m.createdAt).toLocaleTimeString()}</span>
                    </div>
                    <p className="chat-msg-text">{m.text}</p>
                    <button className="chat-msg-hide" onClick={() => handleHide(m.id)} title="Hide message">🚫</button>
                  </div>
                ))}
                <div ref={messagesEnd} />
              </div>
              <div className="chat-input-bar">
                <input className="team-input chat-input" placeholder="Type a message..."
                  value={newMsg} onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()} />
                <button className="neo-btn neo-btn--team" onClick={handleSend}>Send</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
